'use client'

import { useState } from 'react'
import { NewReviewDialog } from './NewReviewDialog'
import { LoginModal } from '@/MainLayout/Header/TopMenu/LoginModal'
import { useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/Auth/useAuth'
import { Button } from '@/General/Button'
import { ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline'
import { useStore } from '@/store'
import { useHasPurchasedProductQuery } from '@/generated/graphql'
import { toastEmitter } from '@/General/Toast'
import { useImpersonationReviewBypass } from '../useImpersonationReviewBypass'

export const AddReview = ({ productId, productName }: AddReviewProps) => {
  const [isNewReviewDialogOpen, setIsNewReviewDialogOpen] = useState(false)
  const [isLoginOpen, setIsLoginOpen] = useState(false)
  const queryClient = useQueryClient()
  const { isAuthenticated } = useAuth()
  const user = useStore(state => state.user)
  const hydrated = useStore(state => state.hydrated)
  const isImpersonationReviewBypass = useImpersonationReviewBypass()

  const handleLoginSuccess = async () => {
    if (isImpersonationReviewBypass) {
      setIsNewReviewDialogOpen(true)
      return
    }
    // After login, check purchase status before opening dialog
    if (user?.id && hydrated) {
      try {
        const purchaseData = await queryClient.fetchQuery({
          queryKey: [
            'HasPurchasedProduct',
            {
              productId: productId,
            },
          ],
          queryFn: () =>
            useHasPurchasedProductQuery.fetcher({
              productId: productId,
            })(),
          staleTime: 5 * 60 * 1000, // Cache for 5 minutes
        })

        if (!purchaseData?.hasPurchasedProduct) {
          toastEmitter.emit('toast', {
            title: 'Purchase Required',
            body: 'You must have purchased this product to write a review.',
            variant: 'error',
          })
          return
        }

        setIsNewReviewDialogOpen(true)
      } catch (error) {
        console.error('Failed to check purchase status:', error)
        toastEmitter.emit('toast', {
          title: 'Error',
          body: 'Unable to verify purchase status. Please try again.',
          variant: 'error',
        })
      }
    } else {
      // If not hydrated yet, just open the dialog (backend will validate)
      setIsNewReviewDialogOpen(true)
    }
  }

  const handleReviewSuccess = () => {
    // Invalidate the reviews query to trigger a refresh
    queryClient.invalidateQueries({ queryKey: ['GetProductBySlug'] })
  }

  const handleReviewClick = async () => {
    if (!isAuthenticated) {
      setIsLoginOpen(true)
      return
    }

    if (isImpersonationReviewBypass) {
      setIsNewReviewDialogOpen(true)
      return
    }

    // Check if user has purchased the product before opening review dialog
    if (user?.id && hydrated) {
      try {
        const purchaseData = await queryClient.fetchQuery({
          queryKey: [
            'HasPurchasedProduct',
            {
              productId: productId,
            },
          ],
          queryFn: () =>
            useHasPurchasedProductQuery.fetcher({
              productId: productId,
            })(),
          staleTime: 5 * 60 * 1000, // Cache for 5 minutes
        })

        if (!purchaseData?.hasPurchasedProduct) {
          toastEmitter.emit('toast', {
            title: 'Purchase Required',
            body: 'You must have purchased this product to write a review.',
            variant: 'error',
          })
          return
        }

        setIsNewReviewDialogOpen(true)
      } catch (error) {
        // If check fails, show error and don't open dialog
        console.error('Failed to check purchase status:', error)
        toastEmitter.emit('toast', {
          title: 'Error',
          body: 'Unable to verify purchase status. Please try again.',
          variant: 'error',
        })
      }
    } else {
      // If not hydrated yet, just open the dialog (backend will validate)
      setIsNewReviewDialogOpen(true)
    }
  }

  return (
    <>
      <Button
        onClick={handleReviewClick}
        color="blue"
        size="md"
        className="flex items-center gap-2"
      >
        <ChatBubbleLeftRightIcon className="w-5 h-5" />
        Write a Review
      </Button>

      <NewReviewDialog
        onSuccess={handleReviewSuccess}
        isOpen={isNewReviewDialogOpen}
        setIsOpen={setIsNewReviewDialogOpen}
        productId={productId}
        productName={productName}
      />
      <LoginModal
        isLoginOpen={isLoginOpen}
        setIsLoginOpen={setIsLoginOpen}
        message="Please log in to write a review."
        onLoginSuccess={handleLoginSuccess}
      />
    </>
  )
}

interface AddReviewProps {
  productId: number
  productName: string
}
